/**
 * Telemedicine · Phase 1 · P1.UI Phase B — "Não foi possível conectar"
 *
 * Generic fatal screen for the lobby/call flow. Matches Figma node
 * 6684:19657. Reusable so app/video-call/[channel].tsx can adopt it later.
 *
 * Layout:
 *   - LobbyHeader chrome (cream, "Agendamento" + close X)
 *   - Centered body: cream camera badge → Sentient title → Inter body copy
 *   - Bottom CTA: dark "Voltar para o início"
 *
 * The badge uses a distinct camera-with-eye glyph (not the simpler badge
 * used by the lobby's headline) — that's the asset the Fatal Figma frame
 * specifies.
 */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import LobbyHeader from './LobbyHeader';

const CREAM = '#F9F8F3';
const INK = '#151515';
const CARARRA = '#F0EEE4';

interface LobbyFatalScreenProps {
  /** Header title. Defaults to "Agendamento" (the Figma copy). */
  headerTitle?: string;
  /** Centered serif headline. Defaults to the Figma copy. */
  title?: string;
  /** Inter body paragraph. Defaults to the Figma copy. */
  body?: string;
  /** CTA label. Defaults to "Voltar para o início". */
  ctaLabel?: string;
  /** Tapping the close icon AND the CTA both call this. */
  onExit: () => void;
}

export default function LobbyFatalScreen({
  headerTitle = 'Agendamento',
  title = 'Não foi possível conectar a essa sessão',
  body = 'Tente novamente em um outro momento ou através de um novo agendamento',
  ctaLabel = 'Voltar para o início',
  onExit,
}: LobbyFatalScreenProps) {
  return (
    <SafeAreaView style={styles.safe}>
      <LobbyHeader title={headerTitle} onClose={onExit} />

      <View style={styles.body}>
        <View style={styles.content}>
          <View style={styles.badge}>
            <RecordVideoCameraIcon />
          </View>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.subtitle}>{body}</Text>
        </View>
      </View>

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.button}
          onPress={onExit}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel={ctaLabel}
        >
          <Text style={styles.buttonText}>{ctaLabel}</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

/**
 * 20×20 record-video glyph from Figma node 642:927: a camera body with the
 * lens "eye" inside, plus the side-flap. Inline because it's only used here
 * and on the channel-fatal screen — not worth a shared asset.
 */
function RecordVideoCameraIcon() {
  return (
    <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
      <Path
        d="M5.1411 4.42627H10.3645C12.1899 4.42627 13.3268 5.71529 13.3268 7.53904V12.4606C13.3268 14.2843 12.1899 15.5733 10.3637 15.5733H5.1411C3.31486 15.5733 2.17969 14.2843 2.17969 12.4606V7.53904C2.17969 5.71529 3.32068 4.42627 5.1411 4.42627Z"
        fill="#FFFFFF"
        stroke={INK}
        strokeWidth={1.84615}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M13.3203 8.45459L15.9407 6.30983C16.3649 5.96221 16.9911 6.02459 17.3387 6.44872C17.4842 6.62668 17.5641 6.84956 17.5632 7.07909L17.5541 12.9262C17.5524 13.4751 17.1075 13.9192 16.5586 13.9175C16.3299 13.9175 16.1079 13.8377 15.9308 13.6921L13.3203 11.5482"
        fill={INK}
        stroke={INK}
        strokeWidth={1.84615}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.83984 10.0016C5.83984 11.0586 6.69641 11.9143 7.75261 11.9143C8.80954 11.9143 9.66615 11.0586 9.66615 10.0016C9.66615 8.94462 8.80954 8.08887 7.75261 8.08887C6.69641 8.08887 5.83984 8.94462 5.83984 10.0016Z"
        stroke={INK}
        strokeWidth={1.84615}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: CREAM },
  body: {
    flex: 1,
    paddingHorizontal: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    paddingHorizontal: 24,
    alignItems: 'center',
    gap: 24,
    width: '100%',
  },
  badge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: CARARRA,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: INK,
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    textAlign: 'center',
  },
  subtitle: {
    color: INK,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 8,
    paddingBottom: 24,
  },
  button: {
    width: '100%',
    height: 56,
    borderRadius: 12,
    backgroundColor: INK,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  buttonText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
});
